import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ScrollPage } from '@/components/layout/PageContainer';
import { useCreateTrunk } from '../api';
import { trunkCreateSchema, type TrunkCreateInput } from '../schema';

export function CreateTrunkPage() {
  const navigate = useNavigate();
  const create = useCreateTrunk();

  const form = useForm<TrunkCreateInput>({
    resolver: zodResolver(trunkCreateSchema),
    mode: 'onTouched',
    defaultValues: { trunkName: '', pilotNumber: '', channels: '', cps: '' },
  });

  const onSubmit = (input: TrunkCreateInput) => {
    create.mutate(input, {
      onSuccess: (res) => {
        toast.success(res?.message ?? 'Trunk created');
        navigate('/trunk-list');
      },
      onError: (err) => toast.error(readErrorMessage(err) ?? 'Could not create the trunk.'),
    });
  };

  return (
    <ScrollPage>
      <PageHeader
        title="Create Trunk"
        description="Configure a SIP trunk for outbound call routing."
        actions={
          <Button asChild variant="outline">
            <Link to="/trunk-list">
              <ArrowLeft className="h-4 w-4" />
              Back to trunks
            </Link>
          </Button>
        }
      />
      <Form {...form}>
        <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)} noValidate>
          <Card>
            <CardHeader>
              <CardTitle>Trunk details</CardTitle>
              <CardDescription>Carrier identifier, pilot number, capacity limits.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="trunkName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Trunk name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g. Tata-Comm-IN-A" autoFocus />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="pilotNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Pilot number</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="+912212345678" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="channels"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Channels</FormLabel>
                    <FormControl>
                      <Input {...field} type="number" inputMode="numeric" min="0" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="cps"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>CPS (calls per second)</FormLabel>
                    <FormControl>
                      <Input {...field} type="number" inputMode="numeric" min="0" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <div className="flex items-center justify-end gap-2">
            <Button asChild variant="ghost">
              <Link to="/trunk-list">Cancel</Link>
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Create trunk
            </Button>
          </div>
        </form>
      </Form>
    </ScrollPage>
  );
}

function readErrorMessage(err: unknown): string | undefined {
  if (err && typeof err === 'object') {
    const a = err as { response?: { data?: { message?: string } }; message?: string };
    return a.response?.data?.message ?? a.message;
  }
  return undefined;
}
